import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Space,
  Spin,
  Empty,
  Alert,
  Card,
  Typography,
  Button,
  theme,
  Input,
  DatePicker,
  Popover,
  Badge,
  Select,
  message,
  Popconfirm,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  FilterOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { HiTemplate, HiDocumentText } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';
import dayjs from '../../../utils/dayjs';
import {
  getFormResponsesByPatient,
  createFormResponse,
  deleteFormResponse,
  getFormResponseDetails,
  updateFormResponse,
} from '../../../actions/formResponseActions';
import { getFormDetails, listForms } from '../../../actions/formActions';
import { FORM_DETAILS_RESET } from '../../../constants/formConstants';
import {
  FORM_RESPONSE_CREATE_RESET,
  FORM_RESPONSE_DELETE_RESET,
  FORM_RESPONSE_UPDATE_RESET,
} from '../../../constants/formResponseConstants';
import FormPreview from '../../clinicalForms/FormPreview.component';
import { getMissingRequiredQuestions } from '../../clinicalForms/utils/formValidation';
import EntityContainer from '../common/EntityContainer.js';
import FormBasedEntityView from '../common/FormBasedEntityView.js';
import FormBasedEntityEdit from '../common/FormBasedEntityEdit.js';
import SectionHeading from '../../common/SectionHeading';
import ApiErrorAlert from '../../../components/common/ApiErrorAlert';

const { Text, Title } = Typography;

const EMPTY_GUID = /^0{8}-0{4}-0{4}-0{4}-0{12}$/i;

const isPatientLevelResponse = (r, patientId) => {
  const pid = String(patientId);
  const type = (r.entityType || '').trim();
  const rawId = r.entityId;
  const eid =
    rawId != null &&
    rawId !== '' &&
    !EMPTY_GUID.test(String(rawId))
      ? String(rawId)
      : null;
  if (!type && !eid) return true;
  const patientTypes = ['fhirpatient', 'patient'];
  if (eid === pid && patientTypes.includes(type.toLowerCase())) return true;
  return false;
};

const PatientDynamicForms = ({
  patientId,
  patientFullName,
  patientDateOfBirth,
  patientYearOfBirth,
  onFormsChange,
  isOneToOne = false,
  includeFormCodes = null,
  excludeFormCodes = null,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { token } = theme.useToken();

  const [containerOpen, setContainerOpen] = useState(false);
  const [mode, setMode] = useState('view');
  /** FormResponse id when viewing or editing */
  const [selectedEntityId, setSelectedEntityId] = useState(null);
  const [createSelectedFormId, setCreateSelectedFormId] = useState(null);
  const [createFormValues, setCreateFormValues] = useState({});
  const [cachedCreateFormDetails, setCachedCreateFormDetails] = useState(null);
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [saveState, setSaveState] = useState({
    saving: false,
    hasChanges: false,
    saveDisabled: true,
  });

  const pendingCreateRef = useRef(false);
  const pendingDeleteRef = useRef(false);
  const deleteTargetIdRef = useRef(null);
  const saveHandlerRef = useRef(() => {});

  const [textFilter, setTextFilter] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const { loading, formResponses, error } = useSelector(
    (state) => state.formResponseByPatient || { loading: false, formResponses: [], error: null }
  );

  const formDetailsState = useSelector((state) => state.formDetails);
  const { form: formDef, loading: loadingForm } = formDetailsState || {};

  const formList = useSelector((state) => state.formList);
  const { forms, loading: loadingForms } = formList || {};

  const formResponseCreate = useSelector((state) => state.formResponseCreate);
  const {
    loading: savingFormResponse,
    success: createFormSuccess,
    formResponse: createdFormResponse,
    error: createFormError,
  } = formResponseCreate || {};

  const formResponseDelete = useSelector((state) => state.formResponseDelete);
  const { success: deleteSuccess, error: deleteError } = formResponseDelete || {};

  const formResponseDetailsForAudit = useSelector((state) => state.formResponseDetails?.formResponse);

  const load = useCallback(() => {
    if (patientId) {
      dispatch(getFormResponsesByPatient(patientId));
    }
  }, [dispatch, patientId]);

  useEffect(() => {
    load();
  }, [load]);

  const resetCreateForm = useCallback(() => {
    setCreateSelectedFormId(null);
    setCreateFormValues({});
    setCachedCreateFormDetails(null);
    setLeftPanelCollapsed(false);
  }, []);

  const handleCloseContainer = useCallback(() => {
    setContainerOpen(false);
    setMode('view');
    setSelectedEntityId(null);
    resetCreateForm();
    pendingCreateRef.current = false;
    pendingDeleteRef.current = false;
    deleteTargetIdRef.current = null;
    setSaveState({ saving: false, hasChanges: false, saveDisabled: true });
    dispatch({ type: FORM_DETAILS_RESET });
    dispatch({ type: FORM_RESPONSE_CREATE_RESET });
    dispatch({ type: FORM_RESPONSE_DELETE_RESET });
    dispatch({ type: FORM_RESPONSE_UPDATE_RESET });
  }, [dispatch, resetCreateForm]);

  useEffect(() => {
    if (!containerOpen || mode !== 'create') return;
    dispatch(listForms({ entityType: 'Patient' }));
  }, [containerOpen, mode, dispatch]);

  useEffect(() => {
    if (!createSelectedFormId || mode !== 'create') return;
    if (!cachedCreateFormDetails || cachedCreateFormDetails.id !== createSelectedFormId) {
      dispatch(getFormDetails(createSelectedFormId));
    }
  }, [createSelectedFormId, cachedCreateFormDetails, mode, dispatch]);

  useEffect(() => {
    if (mode !== 'create' || !formDef || !createSelectedFormId || formDef.id !== createSelectedFormId) return;
    setCachedCreateFormDetails(formDef);
  }, [formDef, createSelectedFormId, mode]);

  const effectiveCreateFormDetails = useMemo(() => {
    if (mode !== 'create') return null;
    if (cachedCreateFormDetails && createSelectedFormId && cachedCreateFormDetails.id === createSelectedFormId) {
      return cachedCreateFormDetails;
    }
    if (formDef && createSelectedFormId && formDef.id === createSelectedFormId) {
      return formDef;
    }
    return null;
  }, [cachedCreateFormDetails, formDef, createSelectedFormId, mode]);

  const createFormSchema = useMemo(() => {
    if (!effectiveCreateFormDetails?.schemaJson) return null;
    try {
      return typeof effectiveCreateFormDetails.schemaJson === 'string'
        ? JSON.parse(effectiveCreateFormDetails.schemaJson)
        : effectiveCreateFormDetails.schemaJson;
    } catch {
      return null;
    }
  }, [effectiveCreateFormDetails]);

  const handleSaveCreate = useCallback(() => {
    if (!createSelectedFormId || !effectiveCreateFormDetails) return;

    const missingRequired = getMissingRequiredQuestions(createFormSchema, createFormValues);
    if (missingRequired.length > 0) {
      message.error(t('Please complete required field(s): {{fields}}', { fields: missingRequired.join(', ') }));
      return;
    }

    const schemaSnapshot = effectiveCreateFormDetails.schemaJson
      ? typeof effectiveCreateFormDetails.schemaJson === 'string'
        ? effectiveCreateFormDetails.schemaJson
        : JSON.stringify(effectiveCreateFormDetails.schemaJson)
      : null;

    pendingCreateRef.current = true;
    dispatch(
      createFormResponse({
        formId: createSelectedFormId,
        formVersion: effectiveCreateFormDetails.version || '1.0.0',
        patientId,
        responseJson: JSON.stringify(createFormValues),
        schemaSnapshot,
        status: 'completed',
      })
    );
  }, [createSelectedFormId, effectiveCreateFormDetails, createFormValues, createFormSchema, patientId, dispatch, t]);

  useEffect(() => {
    saveHandlerRef.current = handleSaveCreate;
  }, [handleSaveCreate]);

  const handleSave = useCallback(() => {
    if (saveHandlerRef.current) saveHandlerRef.current();
  }, []);

  useEffect(() => {
    if (mode !== 'create') return;
    const canCreate = !!createSelectedFormId;
    setSaveState({
      saving: savingFormResponse,
      hasChanges: canCreate,
      saveDisabled: !canCreate,
    });
  }, [mode, createSelectedFormId, savingFormResponse]);

  useEffect(() => {
    if (!createFormSuccess || !createdFormResponse || !pendingCreateRef.current) return;
    pendingCreateRef.current = false;
    message.success(t('Created Successfully'));
    onFormsChange?.();
    load();
    resetCreateForm();
    dispatch({ type: FORM_RESPONSE_CREATE_RESET });
    setSelectedEntityId(createdFormResponse.id ? String(createdFormResponse.id) : null);
    setMode('view');
  }, [createFormSuccess, createdFormResponse, dispatch, load, onFormsChange, resetCreateForm, t]);

  useEffect(() => {
    if (!createFormError || !pendingCreateRef.current) return;
    pendingCreateRef.current = false;
    const text = Array.isArray(createFormError) ? createFormError.join(', ') : createFormError;
    message.error(text || t('Creation Failed'));
    dispatch({ type: FORM_RESPONSE_CREATE_RESET });
  }, [createFormError, dispatch, t]);

  const handleDeleteFormResponse = useCallback(
    (id) => {
      if (!id) return;
      pendingDeleteRef.current = true;
      deleteTargetIdRef.current = id;
      dispatch(deleteFormResponse(id));
    },
    [dispatch]
  );

  useEffect(() => {
    if (!deleteSuccess || !pendingDeleteRef.current) return;
    pendingDeleteRef.current = false;
    const deletedId = deleteTargetIdRef.current;
    deleteTargetIdRef.current = null;
    message.success(t('Deleted Successfully'));
    if (selectedEntityId && String(selectedEntityId) === String(deletedId)) {
      handleCloseContainer();
    } else {
      dispatch({ type: FORM_RESPONSE_DELETE_RESET });
    }
    onFormsChange?.();
    load();
  }, [deleteSuccess, selectedEntityId, handleCloseContainer, dispatch, load, onFormsChange, t]);

  useEffect(() => {
    if (!deleteError || !pendingDeleteRef.current) return;
    pendingDeleteRef.current = false;
    deleteTargetIdRef.current = null;
    const text = Array.isArray(deleteError) ? deleteError.join(', ') : deleteError;
    message.error(text || t('Delete Failed'));
    dispatch({ type: FORM_RESPONSE_DELETE_RESET });
  }, [deleteError, dispatch, t]);

  const updatePatientFormResponseEntity = useCallback((id, data) => async (d) => {
    const patch = {};
    if (data.defaultFormId != null) patch.formId = data.defaultFormId;
    if (data.enabled !== undefined) {
      patch.status = data.enabled ? 'completed' : 'draft';
    }
    if (Object.keys(patch).length > 0) {
      await d(updateFormResponse(id, patch));
    }
  }, []);

  const handleFormEntitySaveSuccess = useCallback(() => {
    onFormsChange?.();
    load();
  }, [load, onFormsChange]);

  const handleSaveStateChange = useCallback((state) => {
    setSaveState(state);
    if (state.onSave) {
      saveHandlerRef.current = state.onSave;
    }
  }, []);

  const patientScoped = useMemo(() => {
    if (!formResponses || !Array.isArray(formResponses)) return [];
    const include = includeFormCodes?.length
      ? new Set(includeFormCodes.map((c) => String(c).toLowerCase()))
      : null;
    const exclude = excludeFormCodes?.length
      ? new Set(excludeFormCodes.map((c) => String(c).toLowerCase()))
      : null;
    return formResponses
      .filter((r) => isPatientLevelResponse(r, patientId))
      .filter((r) => {
        const code = (r.formCode || '').toLowerCase();
        if (include) return include.has(code);
        if (exclude) return !exclude.has(code);
        return true;
      })
      .sort((a, b) => {
        const da = a?.updatedAt ? dayjs(a.updatedAt) : dayjs(0);
        const db = b?.updatedAt ? dayjs(b.updatedAt) : dayjs(0);
        return db.diff(da);
      });
  }, [formResponses, patientId, includeFormCodes, excludeFormCodes]);

  const filteredEntities = useMemo(() => {
    if (!patientScoped.length) return [];

    return patientScoped.filter((entity) => {
      if (textFilter) {
        const searchText = textFilter.toLowerCase();
        const formName = (entity?.formName || t('Form submission')).toLowerCase();
        const dateText = entity?.updatedAt
          ? dayjs(entity.updatedAt).format('DD/MM/YYYY HH:mm').toLowerCase()
          : '';
        const statusText = (entity?.status || '').toLowerCase();

        if (
          !formName.includes(searchText) &&
          !dateText.includes(searchText) &&
          !statusText.includes(searchText)
        ) {
          return false;
        }
      }

      if (dateRange && dateRange.length === 2 && entity?.updatedAt) {
        const updatedDate = dayjs(entity.updatedAt);
        const startDate = dateRange[0].startOf('day');
        const endDate = dateRange[1].endOf('day');

        if (updatedDate.isBefore(startDate) || updatedDate.isAfter(endDate)) {
          return false;
        }
      }

      return true;
    });
  }, [patientScoped, textFilter, dateRange, t]);

  const paginatedEntities = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredEntities.slice(startIndex, endIndex);
  }, [filteredEntities, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [textFilter, dateRange]);

  const handleOpenCreate = () => {
    if (disabled) return;
    dispatch({ type: FORM_RESPONSE_CREATE_RESET });
    dispatch({ type: FORM_RESPONSE_DELETE_RESET });
    dispatch({ type: FORM_RESPONSE_UPDATE_RESET });
    dispatch({ type: FORM_DETAILS_RESET });
    setSelectedEntityId(null);
    resetCreateForm();
    setMode('create');
    setContainerOpen(true);
  };

  const handleView = (response) => {
    if (disabled) return;
    dispatch({ type: FORM_RESPONSE_CREATE_RESET });
    dispatch({ type: FORM_RESPONSE_DELETE_RESET });
    dispatch({ type: FORM_RESPONSE_UPDATE_RESET });
    resetCreateForm();
    setSelectedEntityId(response?.id != null ? String(response.id) : null);
    setMode('view');
    setContainerOpen(true);
  };

  const loadingCreateFormDetails =
    mode === 'create' &&
    !!createSelectedFormId &&
    (!effectiveCreateFormDetails || effectiveCreateFormDetails.id !== createSelectedFormId) &&
    loadingForm;

  const filterContent = (
    <Space direction="vertical" style={{ width: 300 }} size="middle">
      <div>
        <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
          {t('Search')}
        </Text>
        <Input
          placeholder={t('Search...')}
          value={textFilter}
          onChange={(e) => setTextFilter(e.target.value)}
          allowClear
          size="small"
        />
      </div>
      <div>
        <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
          {t('Updated Date Range')}
        </Text>
        <DatePicker.RangePicker
          placeholder={[t('Start date'), t('End date')]}
          value={dateRange}
          onChange={setDateRange}
          format="DD/MM/YYYY"
          size="small"
          allowClear
          style={{ width: '100%' }}
        />
      </div>
      {(textFilter || dateRange) && (
        <Button
          size="small"
          type="link"
          danger
          onClick={() => {
            setTextFilter('');
            setDateRange(null);
          }}
          style={{ padding: 0 }}
        >
          {t('Clear filters')}
        </Button>
      )}
    </Space>
  );

  const leftPanelWidth = leftPanelCollapsed ? 0 : '220px';

  const renderCreateContent = () => (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          icon={leftPanelCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
          size="small"
        >
          {leftPanelCollapsed ? t('Show Panel') : t('Hide Panel')}
        </Button>
      </div>

      <div style={{ display: 'flex', gap: 24, flex: 1, overflow: 'hidden', minHeight: 0 }}>
        {!leftPanelCollapsed && (
          <div
            style={{
              width: leftPanelWidth,
              minWidth: leftPanelWidth,
              overflow: 'auto',
              padding: 16,
              borderRight: `1px solid ${token.colorBorder}`,
              backgroundColor: token.colorFillQuaternary,
              borderRadius: token.borderRadius,
              transition: 'width 0.3s ease',
            }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <div>
                <Space style={{ marginBottom: 12 }}>
                  <HiTemplate size={16} />
                  <Title level={5} style={{ margin: 0, fontSize: 14 }}>
                    {t('Form')}
                  </Title>
                </Space>
                <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 11 }}>
                  {t('Select a form')}
                </Text>
                <Select
                  placeholder={t('Select a form')}
                  style={{ width: '100%' }}
                  value={createSelectedFormId}
                  onChange={(value) => {
                    setCreateSelectedFormId(value);
                    setCreateFormValues({});
                    setCachedCreateFormDetails(null);
                  }}
                  loading={loadingForms}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={(forms || [])
                    .filter((f) => {
                      const code = (f.code || '').toLowerCase();
                      if (includeFormCodes?.length) {
                        return includeFormCodes.map((c) => String(c).toLowerCase()).includes(code);
                      }
                      if (excludeFormCodes?.length) {
                        return !excludeFormCodes.map((c) => String(c).toLowerCase()).includes(code);
                      }
                      return true;
                    })
                    .map((f) => ({
                    value: f.id,
                    label: f.name || f.code,
                  }))}
                />
              </div>
            </Space>
          </div>
        )}

        <div style={{ flex: 1, overflow: 'auto', paddingRight: 8, minHeight: 0 }}>
          {!createSelectedFormId ? (
            <Empty description={t('Select a form template to begin')} style={{ marginTop: 60 }} />
          ) : loadingCreateFormDetails ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <Spin tip={t('Loading form...')} />
            </div>
          ) : createFormSchema && effectiveCreateFormDetails?.id === createSelectedFormId ? (
            <FormPreview
              schema={createFormSchema}
              initialValues={createFormValues}
              onValuesChange={setCreateFormValues}
              compact={false}
              formName={effectiveCreateFormDetails?.name}
              showHeader
              patientFullName={patientFullName}
              patientDateOfBirth={patientDateOfBirth}
              patientYearOfBirth={patientYearOfBirth}
              patientId={patientId}
            />
          ) : null}
        </div>
      </div>
    </div>
  );

  const auditInfo = useMemo(() => {
    if (mode === 'create' || !selectedEntityId || !formResponseDetailsForAudit) return undefined;
    if (String(formResponseDetailsForAudit.id) !== String(selectedEntityId)) return undefined;
    return {
      updatedAt: formResponseDetailsForAudit.updatedAt,
      createdAt: formResponseDetailsForAudit.createdAt,
      updatedBy: formResponseDetailsForAudit.updatedBy,
      createdBy: formResponseDetailsForAudit.createdBy,
    };
  }, [mode, selectedEntityId, formResponseDetailsForAudit]);

  const entityLabel = t('Patient form');

  const getTitle = () => {
    switch (mode) {
      case 'create':
        return `${t('Create')} ${entityLabel}`;
      case 'edit':
        return `${t('Edit')} ${entityLabel}`;
      default:
        return `${entityLabel} ${t('Details')}`;
    }
  };

  const renderContainerContent = () => {
    if (mode === 'create') return renderCreateContent();
    if (!selectedEntityId) {
      return <Empty description={t('No content available')} style={{ marginTop: 48 }} />;
    }
    if (mode === 'edit') {
      return (
        <FormBasedEntityEdit
          entityId={String(selectedEntityId)}
          patientId={patientId}
          getDetails={getFormResponseDetails}
          updateEntity={updatePatientFormResponseEntity}
          listForms={listForms}
          getFormDetails={getFormDetails}
          getFormResponse={getFormResponseDetails}
          createFormResponse={createFormResponse}
          updateFormResponse={updateFormResponse}
          entityStateKey="formResponseDetails"
          entityUpdateKey="formResponseUpdate"
          entityKey="formResponse"
          entityType="patient"
          entityLabel={entityLabel}
          manageEnabled
          formResponseAsEntity
          patientFullName={patientFullName}
          patientDateOfBirth={patientDateOfBirth}
          patientYearOfBirth={patientYearOfBirth}
          onSaveStateChange={handleSaveStateChange}
          onSuccess={handleFormEntitySaveSuccess}
          onView={() => setMode('view')}
        />
      );
    }
    return (
      <FormBasedEntityView
        entityId={String(selectedEntityId)}
        getDetails={getFormResponseDetails}
        getFormDetails={getFormDetails}
        getFormResponse={getFormResponseDetails}
        entityStateKey="formResponseDetails"
        entityKey="formResponse"
        entityLabel={entityLabel}
        formResponseAsEntity
        patientFullName={patientFullName}
        patientDateOfBirth={patientDateOfBirth}
        patientYearOfBirth={patientYearOfBirth}
        patientId={patientId}
        onEdit={disabled ? undefined : () => setMode('edit')}
        onDelete={() => handleDeleteFormResponse(selectedEntityId)}
      />
    );
  };

  return (
    <section
      aria-labelledby="patient-forms-heading"
      style={{
        width: '100%',
        margin: '40px 0',
        paddingTop: 8,
      }}
    >
      <SectionHeading
        id="patient-forms-heading"
        kicker={t('Clinical')}
        title={t('Patient forms')}
        bordered
        extra={
          <Space>
            <Popover
              content={filterContent}
              title={t('Filters')}
              trigger="click"
              placement="bottomRight"
            >
              <Badge dot={!!(textFilter || dateRange)}>
                <Button type="text" icon={<FilterOutlined />} size="small">
                  {t('Filter')}
                </Button>
              </Badge>
            </Popover>
            <Button
              type="text"
              icon={<ReloadOutlined />}
              size="small"
              onClick={load}
              loading={loading}
            >
              {t('Refresh')}
            </Button>
            {!(isOneToOne && patientScoped.length >= 1) && !disabled && (
              <Button type="primary" icon={<PlusOutlined />} size="small" onClick={handleOpenCreate}>
                {t('Add')}
              </Button>
            )}
          </Space>
        }
      />

      <ApiErrorAlert error={error} />

      <Spin spinning={loading}>
        {!loading && filteredEntities.length > 0 ? (
          <>
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              {paginatedEntities.map((r, index) => (
                  <Card
                    key={r?.id || index}
                    hoverable={!disabled}
                    style={{
                      width: '100%',
                      borderRadius: token.borderRadiusLG,
                      opacity: disabled ? 0.65 : 1,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                    }}
                    styles={{ body: { padding: '12px 16px' } }}
                    onClick={() => {
                      if (disabled) return;
                      handleView(r);
                    }}
                  >
                    <Space
                      style={{ width: '100%', justifyContent: 'space-between' }}
                      align="center"
                    >
                      <Space size={12} align="center" style={{ minWidth: 0, flex: 1 }}>
                        <HiDocumentText
                          style={{ fontSize: 18, color: token.colorPrimary, flexShrink: 0 }}
                          aria-hidden
                        />
                        <Space size={6} align="baseline" style={{ minWidth: 0 }} wrap>
                          <Text strong ellipsis style={{ fontSize: 14, margin: 0 }}>
                            {r?.formName || t('Form submission')}
                          </Text>
                          <Text type="secondary" style={{ fontSize: 13 }}>
                            {r?.updatedAt
                              ? dayjs(r.updatedAt).format('DD/MM/YYYY HH:mm')
                              : t('Unknown date')}
                          </Text>
                        </Space>
                      </Space>
                      {!disabled && (
                        <Popconfirm
                          title={t('Delete item?')}
                          description={t('This action cannot be undone.')}
                          onConfirm={(e) => {
                            e?.stopPropagation?.();
                            handleDeleteFormResponse(r.id);
                          }}
                          okText={t('Delete')}
                          cancelText={t('Cancel')}
                          okButtonProps={{ danger: true }}
                        >
                          <Button
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                            onClick={(e) => e.stopPropagation()}
                            aria-label={t('Delete')}
                          />
                        </Popconfirm>
                      )}
                    </Space>
                  </Card>
              ))}
            </Space>

            {filteredEntities.length > pageSize && (
              <div style={{ marginTop: 24, textAlign: 'center' }}>
                <Space direction="vertical" size="small">
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {`${(currentPage - 1) * pageSize + 1}-${Math.min(
                      currentPage * pageSize,
                      filteredEntities.length
                    )} ${t('of')} ${filteredEntities.length} ${t('items')}`}
                  </Text>
                  <Space>
                    <Button
                      size="small"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      {t('Previous')}
                    </Button>
                    <Button
                      size="small"
                      disabled={currentPage * pageSize >= filteredEntities.length}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      {t('Next')}
                    </Button>
                  </Space>
                </Space>
              </div>
            )}
          </>
        ) : !loading ? (
          <Empty
            description={
              textFilter || dateRange
                ? t('No items match the filters')
                : t('No patient forms recorded')
            }
            style={{ padding: '24px 0' }}
          />
        ) : null}
      </Spin>

      <EntityContainer
        containerType="drawer"
        visible={containerOpen}
        onClose={handleCloseContainer}
        title={getTitle()}
        mode={mode}
        content={renderContainerContent()}
        showSaveBar={mode === 'create' || mode === 'edit'}
        saving={saveState.saving}
        hasChanges={saveState.hasChanges}
        onSave={handleSave}
        saveDisabled={saveState.saveDisabled}
        saveText={mode === 'create' ? t('Create') : t('Save')}
        auditInfo={auditInfo}
      />
    </section>
  );
};

export default PatientDynamicForms;
